const DEFAULT_API_URL = 'http://localhost:3000';
const API_REGION_STORAGE_KEY = 'hotelworld-api-region';

const normalizeApiUrl = (url?: string) => url?.trim().replace(/\/+$/, '');

const getStorage = () => {
    try {
        if (typeof window === 'undefined') return undefined;
        return window.localStorage;
    } catch {
        return undefined;
    }
};

export const USERS_API_URL = normalizeApiUrl(process.env.REACT_APP_USERS_API_URL) || DEFAULT_API_URL;
export const USERS_API_URL_US = normalizeApiUrl(process.env.REACT_APP_USERS_API_URL_US);
export const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
export const DOMAIN = process.env.REACT_APP_DOMAIN;
export const DEVICE_INFO_API_URL = normalizeApiUrl(process.env.REACT_APP_DEVICE_INFO_API_URL) || DEFAULT_API_URL;
export const DEVICE_INFO_API_URL_US = normalizeApiUrl(process.env.REACT_APP_DEVICE_INFO_API_URL_US);
export const INVITE_URL = process.env.REACT_APP_INVITE_URL

export type ApiRegion = 'us';

export const normalizeApiRegion = (region?: unknown): ApiRegion | undefined => {
    return typeof region === 'string' && region.trim().toLowerCase() === 'us'
        ? 'us'
        : undefined;
};

export const getApiRegion = (): ApiRegion | undefined => {
    const storage = getStorage();
    return normalizeApiRegion(storage?.getItem(API_REGION_STORAGE_KEY));
};

export const clearApiRegion = () => {
    getStorage()?.removeItem(API_REGION_STORAGE_KEY);
};

export const setApiRegion = (region?: unknown) => {
    const normalizedRegion = normalizeApiRegion(region);
    const storage = getStorage();

    if (!storage) return;

    if (normalizedRegion === 'us') {
        storage.setItem(API_REGION_STORAGE_KEY, normalizedRegion);
        return;
    }

    storage.removeItem(API_REGION_STORAGE_KEY);
};

export const getUsersApiUrl = () => {
    return getApiRegion() === 'us'
        ? USERS_API_URL_US || USERS_API_URL
        : USERS_API_URL;
};

export const getDeviceInfoApiUrl = () => {
    return getApiRegion() === 'us'
        ? DEVICE_INFO_API_URL_US || DEVICE_INFO_API_URL
        : DEVICE_INFO_API_URL;
};

const replaceApiBaseUrl = (
    url: string | undefined,
    activeBaseUrl: string,
    knownBaseUrls: Array<string | undefined>
) => {
    if (!url) return url;

    for (const baseUrl of knownBaseUrls.filter(Boolean) as string[]) {
        if (url === baseUrl) return activeBaseUrl;
        if (url.startsWith(`${baseUrl}/`) || url.startsWith(`${baseUrl}?`)) {
            return `${activeBaseUrl}${url.slice(baseUrl.length)}`;
        }
    }

    return url;
};

export const resolveUsersApiUrl = (url: string | undefined) => {
    return replaceApiBaseUrl(url, getUsersApiUrl(), [USERS_API_URL, USERS_API_URL_US]);
};

export const resolveDeviceInfoApiUrl = (url: string | undefined) => {
    return replaceApiBaseUrl(url, getDeviceInfoApiUrl(), [DEVICE_INFO_API_URL, DEVICE_INFO_API_URL_US]);
};

const getAuthorizeRegion = (payload: any) => {
    const candidates = [
        payload?.rregion,
        payload?.region,
        payload?.data?.rregion,
        payload?.data?.region,
        payload?.data?.user?.rregion,
        payload?.data?.user?.region,
        payload?.data?.companyData?.rregion,
        payload?.data?.companyData?.region,
        payload?.data?.metadata?.user?.rregion,
        payload?.data?.metadata?.user?.region,
        payload?.metadata?.user?.rregion,
        payload?.metadata?.user?.region,
        payload?.user?.rregion,
        payload?.user?.region,
        payload?.companyData?.rregion,
        payload?.companyData?.region,
    ];

    return candidates.find(
        (candidate) => typeof candidate === 'string' && candidate.trim()
    );
};

export const syncApiRegionFromAuthorizeResponse = (payload: any) => {
    setApiRegion(getAuthorizeRegion(payload));
};
